/* eslint-disable max-len */
import { gql } from '@apollo/client';

const BOT_SESSION_FIELDS = `
  id
  created_at
  source
  ai_enabled
  disable_followup
  sales_assigned_id
  metadata
  messages_aggregate {
    aggregate {
      count
    }
  }
  messages (limit: 1, order_by: {created_at: desc}) {
    id
    sender
    text
    created_at
  }
  `;

const BOT_FIELDS = `
    theme_color
    organization_id
    name
    alias
    dark_mode
    avatar_url
    input_placeholder
    language
    id
    limit
    prompt
    default_prompt_id
    deleted_at
    created_at
    personality
    type
    settings
    default_lead_status_id
    unknown_message
    onboarding_complete
    onboarding
    integrations
    support_email
    welcome_message
    whatsapp {
      subscribed
      waba_id
    }
    bot_phone_number {
      active
    }
    initial_questions {
      question
      id
      answer
      category_id
    }
`;

export const GetUserRole = gql`
query GetUserRole ($user_id: String!){
  users_by_pk(id: $user_id) {
    admin
    name
    is_available
  }
}
`;

export const GetCustomerDBInfoByOrgIds = gql`
query GetCustomerDBInfoByOrgIds($organization_ids: [uuid!]!) {
  organizations(where: {id: {_in: $organization_ids}}) {
    id
    name
    customer_email
    customer_phone
    subscriptions {
      id
      is_trial
      expires_at
      monthly_credits
    }
    organization_credits {
      total_credits
      subscription_credits
      purchased_credits
      updated_at
    }
  }
}
`;

export const GetUserDetailsByEmail = gql`
query GetUserDetailsByEmail($email: String!) {
  users(where: {email: {_eq: $email}}) {
    id
    name
    email
    users_organizations {
      rol
      organization_id
      active
      organization {
        id
        name
        customer_id
        customer_email
      }
    }
  }
}
`;

export const GetUserOrganizationsLight = gql`
  query GetUserOrganizationsLight($user_id: String!) {
    users_organizations(where: { user_id: { _eq: $user_id } }) {
      organization {
        id
        name
        customer_id
        customer_email
        reseller
        profile
        logo_url
        users_organizations_aggregate {
          aggregate {
            count
          }
        }
      }
      rol
      organization_id
      bot_id
    }
  }
`;

export const GetOrganizationBotsLight = gql`
  query GetOrganizationBotsLight($organization_id: uuid!) {
    bots(
      where: {
        organization_id: { _eq: $organization_id }
        deleted_at: { _is_null: true }
      }
      order_by: [{ created_at: asc }, { onboarding_complete: asc }]
    ) {
      id
      name
      alias
      avatar_url
      onboarding_complete
      type
      limit
      organization_id
      settings
    }
  }
`;

// Bots across several organizations at once, for screens that can't assume a selected org
// (the Connect-AI consent page: the user may authorize a bot of any org they belong to).
export const GetBotsForOrganizationsLight = gql`
  query GetBotsForOrganizationsLight($organization_ids: [uuid!]!) {
    bots(
      where: {
        organization_id: { _in: $organization_ids }
        deleted_at: { _is_null: true }
      }
      order_by: [{ created_at: asc }]
    ) {
      id
      name
      alias
      organization_id
    }
  }
`;

// Media Library Queries
export const GetBotMediaLibrary = gql`
  query GetBotMediaLibrary($bot_id: uuid!, $file_type: String, $limit: Int, $offset: Int) {
    bot_media_library(
      where: {
        bot_id: { _eq: $bot_id },
        deleted_at: { _is_null: true },
        file_type: { _eq: $file_type }
      },
      limit: $limit,
      offset: $offset,
      order_by: { created_at: desc }
    ) {
      id
      file_name
      file_url
      file_type
      file_size
      mime_type
      created_at
    }
  }
`;

export const GetBotMediaLibraryAll = gql`
  query GetBotMediaLibraryAll($bot_id: uuid!, $limit: Int, $offset: Int) {
    bot_media_library(
      where: {
        bot_id: { _eq: $bot_id },
        deleted_at: { _is_null: true }
      },
      limit: $limit,
      offset: $offset,
      order_by: { created_at: desc }
    ) {
      id
      file_name
      file_url
      file_type
      file_size
      mime_type
      created_at
    }
  }
`;

export const CreateBotMediaLibraryItem = gql`
  mutation CreateBotMediaLibraryItem($item: bot_media_library_insert_input!) {
    insert_bot_media_library_one(object: $item) {
      id
      file_name
      file_url
      file_type
      file_size
      created_at
    }
  }
`;

export const DeleteBotMediaLibraryItem = gql`
  mutation DeleteBotMediaLibraryItem($id: uuid!) {
    update_bot_media_library_by_pk(pk_columns: { id: $id }, _set: { deleted_at: "now()" }) {
      id
      deleted_at
    }
  }
`;

export const GetUserBotRole = gql`
query GetUserBotRole ($user_id: String!, $organization_id: uuid!, $bot_id: uuid!) {
  users_organizations(where: {user_id: {_eq: $user_id}, organization_id: {_eq: $organization_id}, bot_id: {_eq: $bot_id}}) {
    rol
  }
}
`;

export const GetAllOrganizations = gql`
query GetAllOrganizations($limit: Int!, $offset: Int!, $searchTerm: String){
    organizations(
      where: {
        deleted_at: {_is_null: true},
        customer_email: {_is_null: false},
        _or: [
          {name: {_ilike: $searchTerm}},
          {customer_email: {_ilike: $searchTerm}}
        ]
      },
      order_by: {name: asc},
      limit: $limit,
      offset: $offset
    ) {
      id
      name
      customer_email
    }
  }
`;

export const GetBotByPk = gql`
query GetBotByPk ($id: uuid!){
  bots_by_pk(id: $id) {
    ${BOT_FIELDS}
  }
}
`;

export const UpdateBotSettings = gql`
mutation UpdateBotSettings ($id: uuid!, $settings: jsonb!){
  update_bots_by_pk(pk_columns: { id: $id }, _set: { settings: $settings }) {
    settings
  }
}
`;

export const UpdateOrganizationProfile = gql`
mutation UpdateOrganizationProfile ($id: uuid!, $profile: jsonb!){
  update_organizations(where: {id: {_eq: $id}}, _set: {profile: $profile}) {
    affected_rows
  }
}
`;

export const UpdateBotPrompt = gql`
mutation UpdateBotPrompt ($id: uuid!, $updatedPrompt: String!){
  update_bots_by_pk(pk_columns: { id: $id }, _set: { prompt: $updatedPrompt }) {
    prompt
  }
}
`;

export const UpdateBotLimit = gql`
mutation UpdateBotLimit ($id: uuid!, $limit: Int!){
  update_bots_by_pk(pk_columns: { id: $id }, _set: { limit: $limit }) {
    limit
  }
}
`;

export const DeleteBot = gql`
  mutation DeleteBot($id: uuid!) {
    update_bots_by_pk(pk_columns: { id: $id }, _set: { deleted_at: "now()" }) {
      id
    }
  }
`;

// Light check before deleting: the dashboard list does not carry WhatsApp data.
export const GetBotWhatsappStatus = gql`
  query GetBotWhatsappStatus($id: uuid!) {
    bots_by_pk(id: $id) {
      id
      whatsapp {
        subscribed
      }
    }
  }
`;

export const DeleteSession = gql`
  mutation DeleteSession($id: uuid!, $currentTimestamp: timestamp!) {
    update_bot_sessions(where: {id: {_eq: $id}}, _set: {deleted_at: $currentTimestamp}) {
      affected_rows
    }
  }
`;

export const UpdateBot = gql`
  mutation UpdateBot($id: uuid!, $updatedBot: bots_set_input!) {
    update_bots_by_pk(pk_columns: { id: $id }, _set: $updatedBot) {
      ${BOT_FIELDS}
    }
  }
`;

export const UpdateBotType = gql`
  mutation UpdateBotType($id: uuid!, $type: String!) {
    update_bots_by_pk(pk_columns: { id: $id }, _set: { type: $type }) {
      type
    }
  }
`;

export const GetBotTravelCatalog = gql`
query GetBotTravelCatalog ($bot_id: uuid!, $limit: Int!, $offset: Int!, $filter: modules_travel_bool_exp = {}){
  modules_travel(
    where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]},
    order_by: {start_date: asc},
    limit: $limit,
    offset: $offset
  ) {
    web_url
    video_url
    type
    triple_price
    transport
    single_price
    start_date
    quadruple_price
    promotional_price
    place_info
    pdf_url
    origin
    optionals
    nights
    itinerary_url
    itinerary_text
    includes
    child_policy
    conditions
    currency
    days
    destination
    double_price
    end_date
    tags
    hotel
    media_url
    stock
    id
  }
  modules_travel_aggregate(where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]}) {
    aggregate {
      count
    }
  }
}
`;

export const GetBotMachineryCatalog = gql`
query GetBotMachineryCatalog ($bot_id: uuid!, $limit: Int!, $offset: Int!, $filter: modules_catalog_items_bool_exp = {}){
  modules_catalog_items(
    where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]},
    order_by: {created_at: desc},
    limit: $limit,
    offset: $offset
  ) {
    price
    brand
    buying_mode
    category
    media_url
    model
    site_url
    status
    tags
    title
    quantity
    description
    features
    created_at
    item_embeddings
    bot_id
    id
  }
  modules_catalog_items_aggregate(where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]}) {
    aggregate {
      count
    }
  }
}
`;

export const GetBotRealStateCatalog = gql`
query GetBotRealStateCatalog ($bot_id: uuid!, $limit: Int!, $offset: Int!, $filter: modules_real_state_bool_exp = {}){
  modules_real_state(
    where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]},
    order_by: {price: asc},
    limit: $limit,
    offset: $offset
  ) {
    unit
    public_url
    media_url
    system_id
    property_type
    price
    operation_type
    lot_size
    location
    id
    geom
    currency
    construction_size
  }
  modules_real_state_aggregate(where: {_and: [{bot_id: {_eq: $bot_id}}, $filter]}) {
    aggregate {
      count
    }
  }
}
`;

export const GetSessionMessagesNew = gql`
  query GetSessionMessagesNew(
  $bot_id_param: uuid!,
  $offset: Int!,
  $limit: Int!,
  $where: bot_sessions_bool_exp!,
  $order_by: [bot_sessions_order_by!] = [{ last_message_at: desc_nulls_last }, { created_at: desc }]
  ) {
    bot_sessions(
      where: {
        _and: [
          { bot_id: { _eq: $bot_id_param } }
          { deleted_at: { _is_null: true } }
          { demo: { _eq: false } }
          $where
        ]
      }
      order_by: $order_by
      offset: $offset
      limit: $limit
    ) {
      id
      created_at
      bot_id
      source
      metadata
      ai_enabled
      disable_followup
      deleted_at
      messages_viewed
      agent_id
      sales_assigned_id
      last_message_id
      last_message_at

      contact {
        id
        name
        last_name
        phone
        email
        tags
        bot_contacts(where: { bot_id: { _eq: $bot_id_param }, deleted_at: { _is_null: true } }) {
          lead_status_id
        }
      }

      users_messages_viewed {
        unread_count
        session_id
        last_msg_read_id
        user_id
        manual_unread
      }

      last_message {
        id
        text
        created_at
        metadata
        sender
        session_id
        source_id
        sent_at
        delivered_at
        read_at
        failed_at
        deleted_at
        error_code
        error_message
      }
    }
  }
`;

export const GetBotDemoSessions = gql`
query GetBotDemoSessions ($id: uuid!){
  bot_sessions(
    where: {
      bot_id: {_eq: $id},
      demo: {_eq: true},
      _not: {metadata: {_contains: {sherlock_preview: true}}}
    },
    order_by: {created_at: desc}
  ) {
    ${BOT_SESSION_FIELDS}
    deleted_at
    last_message_at
    agent_id
  }
}
`;

export const UpdateDemoSessionName = gql`
  mutation UpdateDemoSessionName($session_id: uuid!, $metadata: jsonb!) {
    update_bot_sessions_by_pk(
      pk_columns: {id: $session_id},
      _set: {metadata: $metadata}
    ) {
      id
      metadata
    }
  }
`;

export const UpdateSessionAiContext = gql`
  mutation UpdateSessionAiContext($session_id: uuid!, $ai_context: String!) {
    update_bot_sessions_by_pk(
      pk_columns: {id: $session_id},
      _set: {ai_context: $ai_context}
    ) {
      id
      ai_context
    }
  }
`;

export const EnableDisableIA = gql`
mutation EnableDisableIA ($id: uuid!, $ai_enabled: Boolean!){
  update_bot_sessions_by_pk(pk_columns: { id: $id }, _set: { ai_enabled: $ai_enabled }) {
    id
  }
}
`;

export const SetSessionDisableFollowup = gql`
mutation SetSessionDisableFollowup($id: uuid!, $disable_followup: Boolean!) {
  update_bot_sessions_by_pk(pk_columns: { id: $id }, _set: { disable_followup: $disable_followup }) {
    id
    disable_followup
  }
}
`;

export const UpdateBotOnboarding = gql`
  mutation UpdateBotOnboarding ($id: uuid!, $updatedOnboarding: jsonb!) {
    update_bots_by_pk(
      pk_columns: { id: $id },
      _set: { onboarding: $updatedOnboarding }
    ) {
      id
    }
  }
`;

export const GetFaqBotQuestionsByAgent = gql`
query GetFAQBotByAgent ($bot_id: uuid!, $limit: Int!, $offset: Int!) {
  bot_flows(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_is_null: true}
    },
    limit: $limit,
    offset: $offset,
    order_by: {id: desc}
  ) {
    id
    message
    intent_keywords
    metadata
    agent_id
  }
  bot_flows_aggregate(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_is_null: true}
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const GetFaqBotQuestionsBySpecificAgent = gql`
query GetFAQBotBySpecificAgent ($bot_id: uuid!, $agent_id: uuid!, $limit: Int!, $offset: Int!) {
  bot_flows(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_eq: $agent_id}
    },
    limit: $limit,
    offset: $offset,
    order_by: {id: desc}
  ) {
    id
    message
    intent_keywords
    metadata
    agent_id
  }
  bot_flows_aggregate(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_eq: $agent_id}
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const CheckBotFaqs = gql`
  query CheckBotFaqs($bot_id: uuid!) {
    bot_flows(where: { bot_id: { _eq: $bot_id }, type: { _eq: "faq" } }) {
      id
    }
  }
`;

export const DeleteBotFaqQuestion = gql`
mutation DeleteBotFaqQuestion ($id: uuid!) {
  delete_bot_flows(where: {id: {_eq: $id}}) {
    affected_rows
  }
}
`;

export const DeleteBotFaqQuestionAll = gql`
mutation DeleteBotFaqQuestionAll ($bot_id: uuid!) {
  delete_bot_flows(where: {bot_id: {_eq: $bot_id}, type: {_eq: "faq"}}) {
    affected_rows
  }
}
`;

export const InsertBotWhatsApp = gql`
mutation InsertBotWhatsApp ($bot_id: uuid!,
  $phone_id: String!, $api_token: String!, $waba_id: String!, $phone_number: String!) {
  insert_bot_whatsapp(objects: {
    bot_id: $bot_id,
    phone_id: $phone_id,
    phone_number: $phone_number,
    api_token: $api_token
    waba_id: $waba_id
  }) {
    returning {
      subscribe_token
    }
  }
}
`;

export const DeleteBotWhatsApp = gql`
mutation DeleteBotWhatsApp ($bot_id: uuid!) {
  delete_bot_whatsapp(where: {bot_id: {_eq: $bot_id}}) {
    affected_rows
  }
}
`;

export const GetBotWhatsApp = gql`
query GetBotWhatsApp ($bot_id: uuid!) {
  bot_whatsapp(where: {bot_id: {_eq: $bot_id}}) {
    subscribed
    phone_id
    subscribe_token
    api_token
    phone_number
    waba_id
  }
}
`;

export const GetBotPhoneNumber = gql`
query GetBotPhoneNumber($bot_id: uuid!) {
  bot_phone_number(where: {bot_id: {_eq: $bot_id}}) {
    active
  }
}
`;

export const InsertBotInitialQuestion = gql`
mutation InsertBotInitialQuestion($answer: String!, $bot_id: uuid!, $question: String!, $category_id: uuid!) {
  insert_bot_initial_questions(objects:
    {answer: $answer, bot_id: $bot_id, question: $question, category_id: $category_id}) {
    affected_rows
    returning {
      id
    }
  }
}
`;

export const DeleteBotInitialQuestion = gql`
mutation DeleteBotInitialQuestion($id: uuid!) {
  delete_bot_initial_questions_by_pk(id: $id) {
    id
  }
}
`;

export const UpdateLeadStatus = gql`
mutation UpdateLeadStatus($id: uuid!, $lead_status_id: uuid!, $bot_id: uuid!) {
  insert_bot_contacts_one(
    object: {
      contact_id: $id,
      bot_id: $bot_id,
      lead_status_id: $lead_status_id
    },
    on_conflict: {
      constraint: bot_contacts_unique,
      update_columns: [lead_status_id]
    }
  ) {
    contact_id
    lead_status_id
  }
}
`;

export const UpdateBotLeadStatus = gql`
mutation UpdateBotLeadStatus ($id: uuid!, $label: String!) {
  update_bot_lead_statuses_by_pk(pk_columns: {id: $id}, _set: {label: $label}) {
    id
  }
}
`;

export const UpdateBotLeadStatusFull = gql`
mutation UpdateBotLeadStatusFull ($id: uuid!, $label: String!, $code: String!) {
  update_bot_lead_statuses_by_pk(pk_columns: {id: $id}, _set: {label: $label, code: $code}) {
    id
    label
    code
  }
}
`;

export const GetFlowsUsingLeadStatus = gql`
query GetFlowsUsingLeadStatus ($bot_id: uuid!, $ref: jsonb!) {
  bot_flows(where: {bot_id: {_eq: $bot_id}, metadata: {_contains: $ref}}) {
    id
    message
  }
}
`;

export const DeleteBotLeadStatus = gql`
mutation DeleteBotLeadStatus ($id: uuid!) {
  delete_bot_lead_statuses(where: {id: {_eq: $id}}) {
    affected_rows
  }
}
`;

export const GetBotLeadsStatus = gql`
query GetBotLeadsStatus ($bot_id: uuid!) {
  bot_lead_statuses(where: {bot_id: {_eq: $bot_id}}, order_by: {sort_order: asc}) {
    id
    label
    sort_order
    code
  }
}
`;

export const GetLeadStatusesByBotIds = gql`
query GetLeadStatusesByBotIds($bot_ids: [uuid!]!) {
  bot_lead_statuses(where: {bot_id: {_in: $bot_ids}}, order_by: {sort_order: asc}) {
    id
    label
    sort_order
    code
    bot_id
  }
}
`;

export const AddBotLeadStatus = gql`
mutation AddBotLeadStatus ($bot_id: uuid!, $label: String!, $code: String!, $sort_order: Int!) {
  insert_bot_lead_statuses_one(object: {bot_id: $bot_id, label: $label, code: $code, sort_order: $sort_order}) {
    id
    label
    code
    sort_order
  }
}
`;

export const InsertBotLeadStatuses = gql`
mutation InsertBotLeadStatuses ($objects: [bot_lead_statuses_insert_input!]!) {
  insert_bot_lead_statuses(objects: $objects) {
    affected_rows
  }
}
`;

export const DeleteDefaultBotLeadStatus = gql`
mutation DeleteDefaultBotLeadStatus ($bot_id: uuid!) {
  delete_bot_lead_statuses(where: {bot_id: {_eq: $bot_id}, code: {_eq: "lead_nuevo"}}) {
    affected_rows
  }
}
`;

export const GetContactData = gql`
query GetContactData ($session_id: uuid!, $bot_id: uuid!) {
  bot_sessions_by_pk(id: $session_id) {
    sales_assigned_id
    disable_followup
    ai_context
    messages(where: { ad_id: { _is_null: false } }, limit: 1) {
      ad_id
    }
    contact {
      email
      id
      name
      last_name
      phone
      website
      address
      city
      state
      country
      postal_code
      notes
      tags
      metadata
      external_refs
      bot_contacts(where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
        lead_status_id
      }
    }
  }
}
`;

export const CheckContactDuplicates = gql`
query CheckContactDuplicates($organization_id: uuid!, $contact_id: uuid!, $bot_id: uuid!, $phone: String, $email: String, $hasPhone: Boolean!, $hasEmail: Boolean!) {
  by_phone: contacts(
    where: {
      organization_id: { _eq: $organization_id },
      id: { _neq: $contact_id },
      phone: { _eq: $phone },
      deleted_at: { _is_null: true }
    },
    limit: 1
  ) @include(if: $hasPhone) {
    ...DupContactFields
  }
  by_email: contacts(
    where: {
      organization_id: { _eq: $organization_id },
      id: { _neq: $contact_id },
      email: { _eq: $email },
      deleted_at: { _is_null: true }
    },
    limit: 1
  ) @include(if: $hasEmail) {
    ...DupContactFields
  }
}
fragment DupContactFields on contacts {
  id
  name
  last_name
  phone
  email
  website
  address
  city
  state
  country
  postal_code
  notes
  tags
  bot_contacts(where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
    lead_status_id
  }
  # WhatsApp-routable sessions: a generated number on a WhatsApp session.
  # WhatsApp sessions use source 'whatsapp' (open/coexistence are channel ids,
  # not session sources); scoping to it excludes phonecall, which also sets
  # recipient_number_generated. count > 0 → phone is the verified WA number.
  wa: bot_sessions_aggregate(
    where: {
      recipient_number_generated: { _is_null: false },
      deleted_at: { _is_null: true },
      source: { _eq: "whatsapp" }
    }
  ) {
    aggregate { count }
  }
}
`;

export const DeleteRefreshToken = gql`
mutation InsertBots($id: uuid!) {
  update_bots(where: {id: {_eq: $id}}, _delete_key: {integrations: "zoho"}) {
    affected_rows
  }
}
`;

export const BotSessionsStream = gql`
  subscription BotSessionsStream($start: timestamp!, $bot_id: uuid!) {
    bot_sessions_stream(
      batch_size: 10,
      cursor: {initial_value: {last_message_at: $start}},
      where: {
        bot_id: {_eq: $bot_id},
        demo: {_eq: false},
        deleted_at: {_is_null: true}
      }
    ) {
      id
      created_at
      bot_id
      source
      metadata
      ai_enabled
      disable_followup
      deleted_at
      messages_viewed

      sales_assigned_id
      last_message_id
      last_message_at

      contact {
        id
        name
        last_name
        phone
        email
        tags
        bot_contacts(where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
          lead_status_id
        }
      }

      users_messages_viewed {
        unread_count
        session_id
        last_msg_read_id
        user_id
        manual_unread
      }

      last_message {
        id
        text
        created_at
        metadata
        sender
        sent_at
        delivered_at
        read_at
        failed_at
        error_code
        error_message
      }
    }
  }
`;

export const BotDemoSessionsStream = gql`
  subscription BotDemoSessionsStream($start: timestamp!, $bot_id: uuid!) {
    bot_sessions_stream(
      batch_size: 10,
      cursor: {initial_value: {last_message_at: $start}},
      where: {
        bot_id: {_eq: $bot_id},
        demo: {_eq: true},
        _not: {metadata: {_contains: {sherlock_preview: true}}}
      }
    ) {
      id
      created_at
      source
      ai_enabled
      disable_followup
      sales_assigned_id
      metadata
      deleted_at
      last_message_at
      agent_id
      messages_aggregate {
        aggregate {
          count
        }
      }
      messages(limit: 1, order_by: {created_at: desc}) {
        id
        sender
        text
        created_at
      }
    }
  }
`;

export const BotSessionsStreamSales = gql`
  subscription BotSessionsStreamSales(
    $user_id: String!
    $start: timestamp!
    $bot_id: uuid!
  ) {
    bot_sessions_stream(
      batch_size: 10,
      cursor: {initial_value: {last_message_at: $start}},
      where: {
        bot_id: {_eq: $bot_id},
        demo: {_eq: false},
        deleted_at: {_is_null: true},
        sales_assigned_id: {_eq: $user_id}
      }
    ) {
      id
      created_at
      bot_id
      source
      metadata
      ai_enabled
      disable_followup
      deleted_at
      messages_viewed

      sales_assigned_id
      last_message_id
      last_message_at

      contact {
        id
        name
        last_name
        phone
        email
        tags
        bot_contacts(where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
          lead_status_id
        }
      }

      users_messages_viewed {
        unread_count
        session_id
        last_msg_read_id
        user_id
        manual_unread
      }

      last_message {
        id
        text
        created_at
        metadata
        sender
        sent_at
        delivered_at
        read_at
        failed_at
        error_code
        error_message
      }
    }
  }
`;

export const BotSessionAssignmentEventsForUser = gql`
  subscription BotSessionAssignmentEventsForUser($since: timestamptz!) {
    bot_session_events(
      where: {
        created_at: {_gt: $since}
        event_category: {_eq: "assignment"}
        is_visible: {_eq: true}
        session_id: {_is_null: false}
      }
      order_by: {created_at: desc}
      limit: 100
    ) {
      id
      session_id
      event_type
      metadata
      created_at
    }
  }
`;

export const GetBotSessionForListing = gql`
  query GetBotSessionForListing($session_id: uuid!) {
    bot_sessions_by_pk(id: $session_id) {
      id
      created_at
      bot_id
      source
      metadata
      ai_enabled
      disable_followup
      deleted_at
      messages_viewed
      sales_assigned_id
      last_message_id
      last_message_at
      contact {
        id
        name
        last_name
        phone
        email
        tags
        bot_contacts(where: { deleted_at: { _is_null: true } }) {
          lead_status_id
          bot_id
        }
      }
      last_message {
        id
        text
        created_at
        metadata
        sender
        sent_at
        delivered_at
        read_at
        failed_at
        error_code
        error_message
      }
      users_messages_viewed {
        unread_count
        session_id
        last_msg_read_id
        user_id
        manual_unread
      }
    }
  }
`;

export const ChatStream = gql`
  subscription ChatStream($start: timestamp, $chat_id: uuid!) {
    messages_stream(batch_size: 10, cursor: {initial_value: {created_at: $start}},
    where: {bot_session: {id: {_eq: $chat_id}}}) {
      created_at
      sender
      text
      id
      metadata
      session_id
      source_id
      sent_at
      delivered_at
      read_at
      failed_at
      deleted_at
      error_code
      error_message
      ad_id
      campaign_id
    }
  }
`;

export const GetChatMessages = gql`
query GetChatMessages($chatId: uuid!) {
  bot_sessions_by_pk(id: $chatId) {
    source
    metadata
    ai_enabled
    disable_followup
    contact {
      id
      name
      last_name
      email
      phone
    }
    messages(order_by: {created_at: asc}) {
      id
      created_at
      text
      sender
      metadata
      ad_id
      campaign_id
      session_id
      source_id
      sent_at
      delivered_at
      read_at
      failed_at
      deleted_at
      error_code
      error_message
      message_feedback {
        id
        message_id
        session_id
        bot_id
        comment
        expected_response
        created_at
      }
    }
    bot_session_events(
      where: { is_visible: { _eq: true } }
      order_by: { created_at: asc }
    ) {
      id
      event_type
      event_category
      description
      metadata
      created_by_type
      created_by_name
      created_at
      severity
    }
    bot_session_notes(
      where: { is_deleted: { _eq: false } }
      order_by: { created_at: asc }
    ) {
      id
      content
      type
      is_pinned
      created_by
      created_at
      updated_at
    }
  }
}
`;

const ChatMessageFields = gql`
  fragment ChatMessageFields on messages {
    id
    created_at
    text
    sender
    metadata
    ad_id
    campaign_id
    session_id
    source_id
    sent_at
    delivered_at
    read_at
    failed_at
    deleted_at
    error_code
    error_message
    message_feedback {
      id
      message_id
      session_id
      bot_id
      comment
      expected_response
      created_at
    }
  }
`;

// Latest page of messages (newest first) plus all visible events and notes.
export const GetLatestChatMessages = gql`
query GetLatestChatMessages($chatId: uuid!, $limit: Int!) {
  bot_sessions_by_pk(id: $chatId) {
    contact {
      id
      name
      last_name
      email
      phone
    }
    messages(order_by: [{created_at: desc}, {id: desc}], limit: $limit) {
      ...ChatMessageFields
    }
    bot_session_events(
      where: { is_visible: { _eq: true } }
      order_by: { created_at: asc }
    ) {
      id
      event_type
      event_category
      description
      metadata
      created_by_type
      created_by_name
      created_at
      severity
    }
    bot_session_notes(
      where: { is_deleted: { _eq: false } }
      order_by: { created_at: asc }
    ) {
      id
      content
      type
      is_pinned
      created_by
      created_at
      updated_at
    }
  }
}
${ChatMessageFields}
`;

// Older page of messages (newest first), strictly before the (created_at, id) cursor.
// The id tiebreaker keeps paging correct when many messages share a timestamp.
export const GetOlderChatMessages = gql`
query GetOlderChatMessages($chatId: uuid!, $before: timestamp!, $beforeId: uuid!, $limit: Int!) {
  messages(
    where: {
      session_id: { _eq: $chatId }
      _or: [
        { created_at: { _lt: $before } }
        { created_at: { _eq: $before }, id: { _lt: $beforeId } }
      ]
    }
    order_by: [{ created_at: desc }, { id: desc }]
    limit: $limit
  ) {
    ...ChatMessageFields
  }
}
${ChatMessageFields}
`;

export const UpdateLastMessageViewed = gql`
mutation UpdateLastMessageViewed($objects: [user_messages_viewed_insert_input!]!) {
  insert_user_messages_viewed(
    objects: $objects,
    on_conflict: {
      constraint: user_messages_viewed_user_id_session_id_key,
      update_columns: [last_msg_read_id, unread_count, updated_at, manual_unread]
    }
  ) {
    returning {
      unread_count
      last_msg_read_id
      updated_at
      manual_unread
    }
  }
}
`;

export const GetBotSession = gql`
query GetBotSession ($session_id: uuid!, $user_id: String!, $bot_id: uuid!){
  bot_sessions(where: {id: {_eq: $session_id}}) {
    ${BOT_SESSION_FIELDS}
    users_messages_viewed (where: {user_id: {_eq: $user_id}}) {
      unread_count
      session_id
      last_msg_read_id
    }
    agent_id
    contact {
      email
      id
      name
      phone
      website
      address
      city
      state
      country
      postal_code
      notes
      tags
      bot_contacts(where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
        lead_status_id
      }
    }
  }
}
`;

export const GetSessionLastMessage = gql`
query GetSessionLastMessage ($session_id: uuid!){
  messages(where: {session_id: {_eq: $session_id}}, order_by: {created_at: desc}, limit: 1) {
    id
    created_at
    text
    sender
    metadata
    session_id
    source_id
    sent_at
    delivered_at
    read_at
    failed_at
    deleted_at
    error_code
    error_message
  }
  bot_sessions_by_pk(id: $session_id) {
    source
    metadata
    messages_viewed
  }
}
`;

export const GetBotFlow = gql`
query GetFlowDiagrams($bot_id: uuid!) {
  flow_diagrams(where: {bot_id: {_eq: $bot_id}}) {
    id
    bot_id
    diagram
    draft_diagram
  }
}
`;

export const GetBotFlowMetadata = gql`
query GetBotFlowsData($bot_id: uuid!) {
  bot_flows(where: {bot_id: {_eq: $bot_id}, type: {_eq: "flow"}}) {
    id
    metadata
  }
}
`;

export const DeleteBotFlow = gql`
mutation DeleteBotFlowFAQs($bot_id: uuid!) {
  delete_bot_flows(where: { bot_id: {_eq: $bot_id}, type: {_eq: "flow"}}) {
    affected_rows
  }
}
`;

export const DeleteFlowDiagram = gql`
mutation DeleteBotFlowFAQs($id: uuid!) {
  delete_flow_diagrams(where: { id: {_eq: $id} }) {
    affected_rows
  }
}
`;

export const UpsertFlowDiagram = gql`
mutation UpdateFlowDiagram ($bot_id: uuid!, $diagram: jsonb!, $draft_diagram: jsonb!) {
  insert_flow_diagrams(objects: {diagram: $diagram, draft_diagram: $draft_diagram, bot_id: $bot_id},
    on_conflict: {constraint: flow_diagrams_bot_id_key, update_columns: [diagram]}
    ) {
    affected_rows
    returning {
      diagram
      draft_diagram
      bot_id
      id
    }
  }
}
`;

export const UpdateDraftDiagram = gql`
mutation InsertFlowDiagrams($id: uuid!, $draft_diagram: jsonb!) {
  update_flow_diagrams(where: {id: {_eq: $id}}, _set: {draft_diagram: $draft_diagram}) {
    affected_rows
    returning {
      bot_id
      diagram
      draft_diagram
      id
    }
  }
}
`;

export const GetOrganizationUsers = gql`
query GetOrganizationUsers($organization_id: uuid!) {
  users_organizations(
    where: {
      organization_id: {_eq: $organization_id},
      active: {_eq: true}
    }
  ) {
    user_id
    bot_id
    user {
      name
      id
      email
      is_available
    }
    rol
  }
}
`;

export const GetOrganizationUsersForBot = gql`
query GetOrganizationUsersForBot($organization_id: uuid!, $bot_id: uuid!) {
  users_organizations(
    where: {
      organization_id: {_eq: $organization_id},
      active: {_eq: true},
      _or: [
        {bot_id: {_eq: $bot_id}},
        {bot_id: {_is_null: true}}
      ]
    }
  ) {
    user_id
    bot_id
    user {
      name
      id
      email
      is_available
    }
    rol
  }
}
`;

export const TravelDeleteSingleProducts = gql`
  mutation TravelDeleteSingleProducts($ids: [uuid!]!) {
    delete_modules_travel(where: {id: {_in: $ids}}) {
      affected_rows
    }
  }
`;

export const ItemsDeleteSingleProducts = gql`
  mutation ItemsDeleteSingleProducts($ids: [uuid!]!) {
    delete_modules_catalog_items(where: {id: {_in: $ids}}) {
      affected_rows
    }
  }
`;

export const RealEstateDeleteSingleProducts = gql`
  mutation ItemsDeleteSingleProducts($ids: [uuid!]!) {
    delete_modules_real_state(where: {id: {_in: $ids}}) {
      affected_rows
    }
  }
`;

export const RemoveUserFromOrganization = gql`
mutation RemoveUserFromOrganization ($org_id: uuid!, $user_id: String!) {
  delete_users_organizations(where: {organization_id: {_eq: $org_id}, user_id: {_eq: $user_id}, rol: {_eq: "superadmin"}}) {
    affected_rows
  }
}
`;

export const CheckOrganizationMembership = gql`
query CheckOrganizationMembership($org_id: uuid!, $user_id: String!) {
  users_organizations(where: {organization_id: {_eq: $org_id}, user_id: {_eq: $user_id}}, limit: 1) {
    user_id
  }
}
`;

export const AddOrganizationSuperAdmin = gql`
mutation AddOrganizationSuperAdmin ($org_id: uuid!, $user_id: String!) {
  insert_users_organizations(objects: {organization_id: $org_id, user_id: $user_id, rol: "superadmin", active: true}) {
    affected_rows
  }
}
`;

export const GetOrgByCustomer = gql`
query GetOrgByCustomer($customer_id: String!) {
  organizations(where: {customer_id: {_eq: $customer_id}}) {
    id
    name
    customer_phone
    subscriptions {
      expires_at
      is_trial
    }
  }
}
`;

export const GetCustomerDBInfo = gql`
query GetCustomerDBInfo($customer_id: String!) {
  organizations(where: {customer_id: {_eq: $customer_id}}) {
    id
    name
    customer_email
    customer_phone
    subscriptions {
      id
      is_trial
      expires_at
      monthly_credits
    }
    organization_credits {
      total_credits
      subscription_credits
      purchased_credits
      updated_at
    }
  }
}
`;

export const GetOrganizationBotsLite = gql`
query GetOrganizationBotsLite ($organization_id: uuid!){
  bots(where: {organization_id: {_eq: $organization_id}, deleted_at: {_is_null: true}}) {
    id
    name
    created_at
  }
}
`;

export const AppendNewIntegration = gql`
mutation InsertBots($bot_id: uuid!, $integration_setting: jsonb!) {
  update_bots(where: {id: {_eq: $bot_id}}, _append: {integrations: $integration_setting}) {
    affected_rows
    returning {
      integrations
    }
  }
}
`;

export const GetOrganizationBotsCounter = gql`
query GetOrganizationBotsCounter ($organization_id: uuid!, $start_date: date!, $end_date: date!) {
  OrganizationBotsCounter(args: {end_date: $end_date, organization_id: $organization_id, start_date: $start_date}) {
    total_sessions
    bot_name
    bot_id
    bot_alias
  }
}
`;

export const GetOrganizationCredits = gql`
query GetOrganizationCredits ($org_id: uuid!) {
  organization_credits(where: {organization_id: {_eq: $org_id}}) {
    updated_at
    total_credits
    subscription_credits
    purchased_credits
  }
  organization_autorecharge(where: {organization_id: {_eq: $org_id}}, limit: 1) {
    enabled
    threshold
    stripe_product_id
    stripe_price_id
    stripe_credits
  }
}
`;

export const GetUserApiKey = gql`
query GetUserApiKey($user_id: String!) {
  users_by_pk(id: $user_id) {
    aspe_api_token
  }
}
`;

export const UpdateUserApiKey = gql`
mutation UpdateUserApiKey($user_id: String!, $api_token: String!) {
  update_users_by_pk(pk_columns: {id: $user_id}, _set: {aspe_api_token: $api_token}) {
    id
  }
}
`;

export const GetSessionBot = gql`
query GetSessionBot($session_id: uuid!) {
  bot_sessions_by_pk(id: $session_id) {
    bot_id
  }
}
`;

export const SearchFaqBotQuestionsByAgent = gql`
query SearchFAQBotByAgent ($bot_id: uuid!, $searchTerm: String!, $limit: Int!, $offset: Int!) {
  bot_flows(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_is_null: true},
      _or: [
        {message: {_ilike: $searchTerm}},
        {intent_keywords: {_ilike: $searchTerm}}
      ]
    },
    limit: $limit,
    offset: $offset,
    order_by: {id: desc}
  ) {
    id
    message
    intent_keywords
    metadata
    agent_id
  }
  bot_flows_aggregate(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_is_null: true},
      _or: [
        {message: {_ilike: $searchTerm}},
        {intent_keywords: {_ilike: $searchTerm}}
      ]
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const SearchFaqBotQuestionsBySpecificAgent = gql`
query SearchFAQBotBySpecificAgent ($bot_id: uuid!, $agent_id: uuid!, $searchTerm: String!, $limit: Int!, $offset: Int!) {
  bot_flows(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_eq: $agent_id},
      _or: [
        {message: {_ilike: $searchTerm}},
        {intent_keywords: {_ilike: $searchTerm}}
      ]
    },
    limit: $limit,
    offset: $offset,
    order_by: {id: desc}
  ) {
    id
    message
    intent_keywords
    metadata
    agent_id
  }
  bot_flows_aggregate(
    where: {
      bot_id: {_eq: $bot_id},
      type: {_eq: "faq"},
      agent_id: {_eq: $agent_id},
      _or: [
        {message: {_ilike: $searchTerm}},
        {intent_keywords: {_ilike: $searchTerm}}
      ]
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const CreateNewContact = gql`
mutation CreateNewContact(
  $organization_id: uuid!,
  $name: String,
  $last_name: String,
  $email: String,
  $phone: String,
  $website: String,
  $address: String,
  $city: String,
  $state: String,
  $country: String,
  $postal_code: String,
  $tags: [String!],
  $notes: String
) {
  insert_contacts_one(object: {
    organization_id: $organization_id,
    name: $name,
    last_name: $last_name,
    email: $email,
    phone: $phone,
    website: $website,
    address: $address,
    city: $city,
    state: $state,
    country: $country,
    postal_code: $postal_code,
    tags: $tags,
    notes: $notes
  }) {
    id
    name
    last_name
    email
    phone
    website
    address
    city
    state
    country
    postal_code
    tags
    notes
  }
}
`;

export const InsertBotContact = gql`
mutation InsertBotContact($contact_id: uuid!, $bot_id: uuid!) {
  insert_bot_contacts_one(
    object: { contact_id: $contact_id, bot_id: $bot_id },
    on_conflict: { constraint: bot_contacts_unique, update_columns: [] }
  ) {
    id
  }
}
`;

export const SearchContactByPhone = gql`
query SearchContactByPhone($organization_id: uuid!, $phone: String!) {
  contacts(
    where: {
      organization_id: { _eq: $organization_id },
      phone: { _eq: $phone }
    },
    limit: 1
  ) {
    id
    name
    last_name
    email
    phone
    website
    address
    city
    state
    country
    postal_code
    tags
    notes
  }
}
`;

export const SearchContactByEmail = gql`
query SearchContactByEmail($organization_id: uuid!, $email: String!) {
  contacts(
    where: {
      organization_id: { _eq: $organization_id },
      email: { _eq: $email }
    },
    limit: 1
  ) {
    id
    name
    last_name
    email
    phone
    website
    address
    city
    state
    country
    postal_code
    tags
    notes
  }
}
`;

export const GetUsersOrganizationUsers = gql`
query GetUsersOrganizationUsers ($organization_id: uuid!){
    users_organizations(where: {organization_id: {_eq: $organization_id}}) {
        rol
        active
        user {
          name
          id
          email
        }
    }
}
`;

export const GetUsersOrganizationSalesUsers = gql`
query GetUsersOrganizationSalesUsers ($organization_id: uuid!, $bot_id: uuid!){
    users_organizations(where: {
      organization_id: {_eq: $organization_id},
      rol: {_eq: "sales"},
      _or: [
        {bot_id: {_eq: $bot_id}},
        {bot_id: {_is_null: true}}
      ]
    }) {
        user_id
        rol
        user {
          name
          id
          email
          is_available
        }
    }
}
`;

export const InsertSalesAssignments = gql`
  mutation InsertSalesAssignments($objects: [sales_assignments_insert_input!]!) {
    insert_sales_assignments(objects: $objects) {
      affected_rows
    }
  }
`;

export const DeleteSalesAssignment = gql`
  mutation DeleteSalesAssignment($id: uuid!) {
    delete_sales_assignments(where: {id: {_eq: $id}}) {
      affected_rows
    }
  }
`;

export const GetUserAssignmentsCount = gql`
  query GetUserAssignmentsCount($user_id: String!, $organization_id: uuid!) {
    bot_sessions_aggregate(
      where: {
        sales_assigned_id: {_eq: $user_id},
        deleted_at: {_is_null: true},
        bot: {
          organization_id: {_eq: $organization_id}
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetUserAssignedBotSessions = gql`
  query GetUserAssignedBotSessions($sales_assigned_id: String!, $organization_id: uuid!) {
    bot_sessions(
      where: {
        sales_assigned_id: {_eq: $sales_assigned_id},
        deleted_at: {_is_null: true},
        bot: {
          organization_id: {_eq: $organization_id}
        }
      }
    ) {
      id
      bot_id
      sales_assigned_id
      contact {
        id
        phone
        name
        email
      }
    }
  }
`;

export const UpdateChatUnread = gql`
  mutation UpdateChatUnread($session_id: uuid!, $manual_unread: Boolean!, $user_id: String!) {
    update_user_messages_viewed(where: {session_id: {_eq: $session_id}, user_id: {_eq: $user_id}}, _set: {manual_unread: $manual_unread}) {
      affected_rows
    }
  }
`;

export const GetOrganizationContactTags = gql`
query GetOrganizationContactTags($organization_id: uuid!, $bot_id: uuid) {
  get_organization_contact_tags(args: {organization_id: $organization_id, bot_id: $bot_id}) {
    tag
    contacts_count
  }
}
`;

export const GetQuickReplyTags = gql`
query GetQuickReplyTags($bot_id: uuid!) {
  quick_replies(
    where: {
      bot_id: { _eq: $bot_id }
      deleted_at: { _is_null: true }
      tags: { _is_null: false }
    }
    distinct_on: []
  ) {
    tags
  }
}
`;

export const GetBotUnassignedContactsWithTags = gql`
query GetBotUnassignedContactsWithTags ($bot_id: uuid!, $limit: Int!, $offset: Int!, $tags: [String!]) {
  contacts(
    where: {
      deleted_at: {_is_null: true},
      bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
      tags: {_contains: $tags},
      phone: {_is_null: false},
      _not: {
        sales_assignments: {
          active: {_eq: true}
        }
      }
    },
    limit: $limit,
    offset: $offset,
    order_by: {created_at: desc}
  ) {
    last_name
    phone
    notes
    name
    id
    email
    city
    country
    created_at
    address
    tags
    website
    bot_sessions(limit: 1, order_by: {last_message_at: desc_nulls_last}) {
      id
    }
  }
  contacts_aggregate(
    where: {
      deleted_at: {_is_null: true},
      bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
      tags: {_contains: $tags},
      phone: {_is_null: false},
      _not: {
        sales_assignments: {
          active: {_eq: true}
        }
      }
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const GetBotUnassignedContacts = gql`
query GetBotUnassignedContacts ($bot_id: uuid!, $limit: Int!, $offset: Int!) {
  contacts(
    where: {
      deleted_at: {_is_null: true},
      bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
      phone: {_is_null: false},
      _not: {
        sales_assignments: {
          active: {_eq: true}
        }
      }
    },
    limit: $limit,
    offset: $offset,
    order_by: {created_at: desc}
  ) {
    last_name
    phone
    notes
    name
    id
    email
    city
    country
    created_at
    address
    tags
    website
    bot_sessions(limit: 1, order_by: {last_message_at: desc_nulls_last}) {
      id
    }
  }
  contacts_aggregate(
    where: {
      deleted_at: {_is_null: true},
      bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
      phone: {_is_null: false},
      _not: {
        sales_assignments: {
          active: {_eq: true}
        }
      }
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const GetSandboxRoutedSession = gql`
  query GetSandboxRoutedSession($bot_id: uuid!) {
    bot_sessions(
      where: {
        metadata: { _contains: { routed_bot_id: $bot_id } },
        deleted_at: { _is_null: true }
      }
      limit: 1
    ) {
      id
    }
  }
`;

export const GetBotUnassignedContactsWithConversation = gql`
  query GetBotUnassignedContactsWithConversation($bot_id: uuid!, $limit: Int, $offset: Int) {
    contacts(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        bot_sessions: {},
        _not: {
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
      limit: $limit
      offset: $offset
      order_by: {created_at: desc}
    ) {
      last_name
      phone
      notes
      name
      id
      email
      city
      country
      created_at
      address
      tags
      website
      bot_sessions(limit: 1, order_by: {last_message_at: desc_nulls_last}) {
        id
      }
    }
    contacts_aggregate(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        bot_sessions: {},
        _not: {
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetBotUnassignedContactsWithoutConversation = gql`
  query GetBotUnassignedContactsWithoutConversation($bot_id: uuid!, $limit: Int, $offset: Int) {
    contacts(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        _not: {
          bot_sessions: {},
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
      limit: $limit
      offset: $offset
      order_by: {created_at: desc}
    ) {
      last_name
      phone
      notes
      name
      id
      email
      city
      country
      created_at
      address
      tags
      website
    }
    contacts_aggregate(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        _not: {
          bot_sessions: {},
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetBotUnassignedContactsWithTagsAndWithConversation = gql`
  query GetBotUnassignedContactsWithTagsAndWithConversation($bot_id: uuid!, $limit: Int, $offset: Int, $tags: [String!]) {
    contacts(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        tags: {_contains: $tags},
        bot_sessions: {},
        _not: {
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
      limit: $limit
      offset: $offset
      order_by: {created_at: desc}
    ) {
      last_name
      phone
      notes
      name
      id
      email
      city
      country
      created_at
      address
      tags
      website
      bot_sessions(limit: 1, order_by: {last_message_at: desc_nulls_last}) {
        id
      }
    }
    contacts_aggregate(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        tags: {_contains: $tags},
        bot_sessions: {}
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetBotUnassignedContactsWithTagsAndWithoutConversation = gql`
  query GetBotUnassignedContactsWithTagsAndWithoutConversation($bot_id: uuid!, $limit: Int, $offset: Int, $tags: [String!]) {
    contacts(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        tags: {_contains: $tags},
        _not: {
          bot_sessions: {},
          sales_assignments: {
            active: {_eq: true}
          }
        }
      }
      limit: $limit
      offset: $offset
      order_by: {created_at: desc}
    ) {
      last_name
      phone
      notes
      name
      id
      email
      city
      country
      created_at
      address
      tags
      website
    }
    contacts_aggregate(
      where: {
        deleted_at: {_is_null: true},
        bot_contacts: {bot_id: {_eq: $bot_id}, deleted_at: {_is_null: true}},
        phone: {_is_null: false},
        tags: {_contains: $tags},
        _not: {
          bot_sessions: {}
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// Ids only, same where-clause as GetBotContactsByStatus: selecting a whole column
// must resolve to exactly the contacts the board is painting, never more.
export const GetBotContactIdsByStatus = gql`
query GetBotContactIdsByStatus(
  $bot_id: uuid!,
  $lead_status_id: uuid!,
  $limit: Int!,
  $offset: Int!,
  $filter: [contacts_bool_exp!] = []
) {
  contacts(
    where: {
      deleted_at: { _is_null: true },
      bot_contacts: { bot_id: { _eq: $bot_id }, lead_status_id: { _eq: $lead_status_id }, deleted_at: { _is_null: true } },
      _and: $filter
    },
    limit: $limit,
    offset: $offset,
    order_by: [{ created_at: desc }, { id: asc }]
  ) {
    id
  }
}
`;

export const GetBotContactsByStatus = gql`
query GetBotContactsByStatus(
  $bot_id: uuid!,
  $lead_status_id: uuid!,
  $limit: Int!,
  $offset: Int!,
  $filter: [contacts_bool_exp!] = [],
  $order_by: [contacts_order_by!] = [{ created_at: desc }]
) {
  contacts(
    where: {
      deleted_at: { _is_null: true },
      bot_contacts: { bot_id: { _eq: $bot_id }, lead_status_id: { _eq: $lead_status_id }, deleted_at: { _is_null: true } },
      _and: $filter
    },
    limit: $limit,
    offset: $offset,
    order_by: $order_by
  ) {
    id
    name
    last_name
    email
    phone
    tags
    created_at
    address
    city
    country
    website
    notes
    bot_sessions(limit: 1, order_by: { last_message_at: desc_nulls_last }, where: { bot_id: { _eq: $bot_id }, deleted_at: { _is_null: true } }) {
      id
      agent_id
      sales_assigned_id
      ai_enabled
    }
  }
  contacts_aggregate(
    where: {
      deleted_at: { _is_null: true },
      bot_contacts: { bot_id: { _eq: $bot_id }, lead_status_id: { _eq: $lead_status_id }, deleted_at: { _is_null: true } },
      _and: $filter
    }
  ) {
    aggregate {
      count
    }
  }
}
`;

export const OrganizationMonthlyConsumption = gql`
  query OrganizationMonthlyConsumption($start_date: timestamp!, $end_date: timestamp!) {
    organization_monthly_consumption(args: {start_date: $start_date, end_date: $end_date}) {
      organization_id
      organization_name
      month
      conversation_count
      monthly_credits
    }
  }
`;

export const OrganizationMonthlyConsumptionAll = gql`
  query OrganizationMonthlyConsumptionAll($start_date: timestamp!, $end_date: timestamp!) {
    organization_monthly_consumption_all(args: {start_date: $start_date, end_date: $end_date}) {
      organization_id
      organization_name
      month
      conversation_count
      monthly_credits
    }
  }
`;

export const UpdateContactTags = gql`
mutation UpdateContactTags($id: uuid!, $tags: [String!]!) {
  update_contacts(where: {id: {_eq: $id}}, _set: {tags: $tags}) {
    affected_rows
  }
}
`;

export const GetTrialMetadataDashboard = gql`
  query GetTrialMetadataDashboard($limit: Int!, $offset: Int!, $where: trial_metadata_bool_exp) {
    trial_metadata_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    trial_metadata(
      where: $where,
      limit: $limit,
      offset: $offset,
      order_by: [
        {trial_end: asc_nulls_last}
      ]
    ) {
      id
      organization_id
      bant_need
      bant_authority
      bant_budget
      bant_timeline
      trial_end
      contacted
      organization {
        name
        customer_email
        customer_id
        customer_phone
      }
    }
  }
`;

export const InsertSubscriptionCancellation = gql`
  mutation InsertSubscriptionCancellation($objects: [subscription_cancellations_insert_input!]!) {
    insert_subscription_cancellations(objects: $objects) {
      affected_rows
    }
  }
`;

export const UpdateTrialMetadataContacted = gql`
  mutation UpdateTrialMetadataContacted($id: uuid!, $contacted: Boolean!) {
    update_trial_metadata_by_pk(pk_columns: {id: $id}, _set: {contacted: $contacted}) {
      id
      contacted
    }
  }
`;

export const UpdateIsTrial = gql`
  mutation UpdateIsTrial($org_id: uuid!, $is_trial: Boolean) {
    update_subscriptions(where: {organization_id: {_eq: $org_id}}, _set: {is_trial: $is_trial}) {
      affected_rows
    }
  }
`;

export const UpdateExpiresAt = gql`
  mutation UpdateExpiresAt($org_id: uuid!, $expires_at: timestamp!) {
    update_subscriptions(where: {organization_id: {_eq: $org_id}}, _set: {expires_at: $expires_at}) {
      affected_rows
    }
  }
`;

export const GetAllSubscriptionsWithOrganization = gql`
  query GetAllSubscriptionsWithOrganization {
    subscriptions(where: {
      _or: [
        {is_trial: {_eq: true}},
        {is_trial: {_eq: false}}
      ]
    }) {
      id
      is_trial
      expires_at
      organization {
        id
        name
        customer_id
        customer_email
      }
    }
  }
`;

export const UpdateOrganizationCustomerPhone = gql`
  mutation UpdateOrganizationCustomerPhone($org_id: uuid!, $phone: String) {
    update_organizations(where: {id: {_eq: $org_id}}, _set: {customer_phone: $phone}) {
      affected_rows
    }
  }
`;

export const GetContactExternalRefs = gql`
  query GetContactExternalRefs($id: uuid!) {
    contacts_by_pk(id: $id) {
      id
      external_refs
    }
  }
`;

export const GetContactIdsByPhone = gql`
  query GetContactIdsByPhone($phone: String!) {
    contacts(where: { phone: { _eq: $phone } }) {
      id
    }
  }
`;

export const UpdateBotContactLeadStatusByContactIds = gql`
  mutation UpdateBotContactLeadStatusByContactIds(
    $contact_ids: [uuid!]!
    $bot_id: uuid!
    $lead_status_id: uuid!
  ) {
    update_bot_contacts(
      where: { bot_id: { _eq: $bot_id }, contact_id: { _in: $contact_ids } }
      _set: { lead_status_id: $lead_status_id }
    ) {
      affected_rows
    }
  }
`;

export const UpdateLeadStatusesOrder = gql`
  mutation UpdateLeadStatusesOrder($updates: [bot_lead_statuses_insert_input!]!) {
    insert_bot_lead_statuses(
      objects: $updates,
      on_conflict: {
        constraint: bot_lead_statuses_pkey,
        update_columns: [sort_order]
      }
    ) {
      affected_rows
    }
  }
`;

export const BulkUpdateContactsLeadStatus = gql`
  mutation BulkUpdateContactsLeadStatus(
    $source_lead_status_id: uuid!,
    $target_lead_status_id: uuid!,
    $bot_id: uuid!
  ) {
    update_bot_contacts(
      where: {
        lead_status_id: { _eq: $source_lead_status_id },
        bot_id: { _eq: $bot_id }
      },
      _set: {
        lead_status_id: $target_lead_status_id
      }
    ) {
      affected_rows
    }
  }
`;

export const UpdateBotPromptByPk = gql`
  mutation UpdateBotPromptByPk($id: uuid!, $name: String!, $prompt: String!, $circumstances: String!, $auto_add_to_orchestrator: Boolean!) {
    update_bot_prompts_by_pk(
      pk_columns: { id: $id }
      _set: { name: $name, prompt: $prompt, circumstances: $circumstances, auto_add_to_orchestrator: $auto_add_to_orchestrator }
    ) {
      id
      name
      prompt
      circumstances
      auto_add_to_orchestrator
    }
  }
`;

export const GetAllOrganizationsForAudit = gql`
  query GetAllOrganizationsForAudit {
    organizations(where: {deleted_at: {_is_null: true}}) {
      id
      customer_id
      customer_email
    }
  }
`;

export const GetBotPrompts = gql`
query GetBotPrompts ($bot_id: uuid!) {
  bot_prompts(where: {bot_id: {_eq: $bot_id}}) {
    id
    name
    slug
    prompt
    use_context
    created_at
    circumstances
    auto_add_to_orchestrator
    bot_prompts_triggers {
      id
      type
      value
    }
  }
}
`;

export const DeleteBotPrompt = gql`
mutation DeleteBotPrompt($id: uuid!) {
  delete_bot_prompts_by_pk(id: $id) {
    id
  }
}
`;

export const ClearAgentFromSessionsByPrompt = gql`
mutation ClearAgentFromSessionsByPrompt($agent_id: uuid!) {
  update_bot_sessions(where: {agent_id: {_eq: $agent_id}}, _set: {agent_id: null}) {
    affected_rows
  }
}
`;

export const QUICK_REPLY_FIELDS = gql`
  fragment QuickReplyFields on quick_replies {
    id
    bot_id
    user_id
    name
    text
    is_shared
    tags
    created_at
    updated_at
    deleted_at
  }
`;

export const InsertQuickReply = gql`
  mutation InsertQuickReply(
    $id: uuid!,
    $bot_id: uuid!,
    $user_id: String!,
    $name: String!,
    $text: String!,
    $is_shared: Boolean!,
    $tags: [String!],
    $created_at: timestamptz!,
    $updated_at: timestamptz!
  ) {
    insert_quick_replies_one(object: {
      id: $id,
      bot_id: $bot_id,
      user_id: $user_id,
      name: $name,
      text: $text,
      is_shared: $is_shared,
      tags: $tags,
      created_at: $created_at,
      updated_at: $updated_at
    }) {
      ...QuickReplyFields
    }
  }
  ${QUICK_REPLY_FIELDS}
`;

export const UpdateQuickReply = gql`
  mutation UpdateQuickReply(
    $id: uuid!,
    $set_input: quick_replies_set_input!
  ) {
    update_quick_replies_by_pk(
      pk_columns: { id: $id },
      _set: $set_input
    ) {
      ...QuickReplyFields
    }
  }
  ${QUICK_REPLY_FIELDS}
`;

export const SoftDeleteQuickReply = gql`
  mutation SoftDeleteQuickReply($id: uuid!, $deleted_at: timestamptz!, $updated_at: timestamptz!) {
    update_quick_replies_by_pk(
      pk_columns: { id: $id },
      _set: { deleted_at: $deleted_at, updated_at: $updated_at }
    ) {
      id
      deleted_at
      updated_at
    }
  }
`;

export const GetQuickRepliesForUser = gql`
  query GetQuickRepliesForUser(
    $bot_id: uuid!,
    $limit: Int!,
    $offset: Int!,
    $filter_condition: [quick_replies_bool_exp!],
    $order_by: [quick_replies_order_by!],
    $where_conditions: [quick_replies_bool_exp!]
  ) {
    quick_replies(
      where: {
        bot_id: { _eq: $bot_id }
        deleted_at: { _is_null: true }
        _and: $where_conditions
      }
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...QuickReplyFields
    }
  }
  ${QUICK_REPLY_FIELDS}
`;
export const UpdateAgentAvailability = gql`
  mutation UpdateAgentAvailability($user_id: String!, $is_available: Boolean!) {
    update_users_by_pk(pk_columns: { id: $user_id }, _set: { is_available: $is_available }) {
      id
      is_available
    }
  }
`;

export const GetBotSessionsInBillingCycle = gql`
  query GetBotSessionsInBillingCycle($bot_id: uuid!, $start_date: timestamp!, $end_date: timestamp!) {
    bot_sessions_aggregate(
      where: {
        bot_id: { _eq: $bot_id },
        deleted_at: { _is_null: true },
        demo: { _eq: false },
        created_at: { _gte: $start_date, _lte: $end_date }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// ==================== NOTES QUERIES ====================

export const GetSessionNotes = gql`
  query GetSessionNotes($session_id: uuid!) {
    bot_session_notes(
      where: {
        session_id: { _eq: $session_id }
        is_deleted: { _eq: false }
      }
      order_by: [
        { is_pinned: desc }
        { created_at: desc }
      ]
    ) {
      id
      session_id
      contact_id
      content
      type
      is_pinned
      is_deleted
      created_by
      created_at
      updated_at
      deleted_at
    }
  }
`;

export const CreateNote = gql`
  mutation CreateNote(
    $session_id: uuid!,
    $contact_id: uuid,
    $content: String!,
    $type: String!,
    $is_pinned: Boolean,
    $created_by: String!
  ) {
    insert_bot_session_notes_one(
      object: {
        session_id: $session_id
        contact_id: $contact_id
        content: $content
        type: $type
        is_pinned: $is_pinned
        created_by: $created_by
      }
    ) {
      id
      session_id
      contact_id
      content
      type
      is_pinned
      is_deleted
      created_by
      created_at
      updated_at
    }
  }
`;

export const UpdateNote = gql`
  mutation UpdateNote($id: uuid!, $content: String!) {
    update_bot_session_notes_by_pk(
      pk_columns: { id: $id }
      _set: { content: $content }
    ) {
      id
      content
      updated_at
    }
  }
`;

export const UpdateNoteType = gql`
  mutation UpdateNoteType($id: uuid!, $type: String!) {
    update_bot_session_notes_by_pk(
      pk_columns: { id: $id }
      _set: { type: $type }
    ) {
      id
      type
    }
  }
`;

export const DeleteNote = gql`
  mutation DeleteNote($id: uuid!) {
    update_bot_session_notes_by_pk(
      pk_columns: { id: $id }
      _set: {
        is_deleted: true
        deleted_at: "now()"
      }
    ) {
      id
      is_deleted
      deleted_at
    }
  }
`;

export const PinNote = gql`
  mutation PinNote($id: uuid!, $is_pinned: Boolean!) {
    update_bot_session_notes_by_pk(
      pk_columns: { id: $id }
      _set: { is_pinned: $is_pinned }
    ) {
      id
      is_pinned
    }
  }
`;

// ==================== EVENTS QUERIES ====================

export const GetSessionEvents = gql`
  query GetSessionEvents($session_id: uuid!) {
    bot_session_events(
      where: {
        session_id: { _eq: $session_id }
        is_visible: { _eq: true }
      }
      order_by: [
        { created_at: desc }
      ]
    ) {
      id
      session_id
      event_type
      event_category
      description
      metadata
      created_by
      created_by_type
      created_by_name
      created_at
      severity
    }
  }
`;

export const GetSessionAiContext = gql`
  query GetSessionAiContext($session_id: uuid!) {
    bot_sessions_by_pk(id: $session_id) {
      ai_context
    }
  }
`;

export const CreateSessionEvent = gql`
  mutation CreateSessionEvent(
    $session_id: uuid!,
    $event_type: String!,
    $event_category: String!,
    $description: String!,
    $metadata: jsonb,
    $created_by: String,
    $created_by_type: String!,
    $created_by_name: String,
    $severity: String!
  ) {
    insert_bot_session_events_one(object: {
      session_id: $session_id
      event_type: $event_type
      event_category: $event_category
      description: $description
      metadata: $metadata
      created_by: $created_by
      created_by_type: $created_by_type
      created_by_name: $created_by_name
      severity: $severity
    }) {
      id
      created_at
    }
  }
`;

// ==================== CHAT CONFIG QUERIES ====================

export const GetBotChatConfig = gql`
  query GetBotChatConfig($bot_id: uuid!, $config_type: String!) {
    bot_chat_configs(where: {bot_id: {_eq: $bot_id}, config_type: {_eq: $config_type}}) {
      id
      bot_id
      config_type
      enabled
      settings
      created_at
      updated_at
    }
  }
`;

export const UpsertBotChatConfig = gql`
  mutation UpsertBotChatConfig($bot_id: uuid!, $config_type: String!, $enabled: Boolean!, $settings: jsonb!) {
    insert_bot_chat_configs(
      objects: {bot_id: $bot_id, config_type: $config_type, enabled: $enabled, settings: $settings}
      on_conflict: {constraint: bot_chat_configs_bot_id_config_type_key, update_columns: [enabled, settings, updated_at]}
    ) {
      affected_rows
      returning {
        id
        bot_id
        config_type
        enabled
        settings
        updated_at
      }
    }
  }
`;

// ==================== MCP QUERIES ====================

export const GetMCPServers = gql`
  query GetMCPServers {
    mcp_servers(order_by: { created_at: desc }) {
      id
      name
      url
      type
      is_public
      available_tools
      created_at
      updated_at
    }
  }
`;

export const GetBotMCPServers = gql`
  query GetBotMCPServers($bot_id: uuid!) {
    bot_mcp_servers(
      where: { bot_id: { _eq: $bot_id } }
      order_by: { created_at: desc }
    ) {
      id
      bot_id
      server_id
      enabled_tools
      is_active
      parameters
      created_at
      updated_at
    }
  }
`;

export const GetBotMCPServersWithTools = gql`
  query GetBotMCPServersWithTools($bot_id: uuid!) {
    bot_mcp_servers(
      where: { bot_id: { _eq: $bot_id }, is_active: { _eq: true } }
      order_by: { created_at: desc }
    ) {
      id
      bot_id
      server_id
      enabled_tools
      is_active
      parameters
      mcp_server {
        id
        name
        url
        type
        available_tools
      }
    }
  }
`;

export const UpdateBotMCPServer = gql`
  mutation UpdateBotMCPServer($id: uuid!, $changes: bot_mcp_servers_set_input!) {
    update_bot_mcp_servers_by_pk(pk_columns: { id: $id }, _set: $changes) {
      id
      bot_id
      server_id
      enabled_tools
      is_active
      parameters
      created_at
      updated_at
    }
  }
`;

export const GetSpamSessions = gql`
  query GetSpamSessions($where: bot_sessions_bool_exp!, $limit: Int!, $offset: Int!) {
    bot_sessions(
      where: {
        _and: [
          $where
          { deleted_at: { _is_null: true } }
          { demo: { _eq: false } }
        ]
      }
      order_by: { last_message_at: desc_nulls_last, created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      id
      bot_id
      last_message_at
      ai_enabled
      bot {
        id
        name
        organization_id
      }
      contact {
        id
        name
        last_name
        phone
        tags
      }
    }
  }
`;

export const GetSpamSessionsCount = gql`
  query GetSpamSessionsCount($where: bot_sessions_bool_exp!) {
    bot_sessions_aggregate(
      where: {
        _and: [
          $where
          { deleted_at: { _is_null: true } }
          { demo: { _eq: false } }
        ]
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;
